/**
 * @file CommandSearch.tsx
 * @description Global module search palette triggered by Ctrl+K / ⌘K.
 *              Searches all sidebar nav items and navigates on selection.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Lock } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { getFlatNavItems, type NavItem } from './nav-items';
import { cn } from '@/lib/utils';
import useUIStore from '@/store/uiStore';
import { PinVerificationModal } from '@/components/common/PinVerificationModal';

const FLAT_ITEMS = getFlatNavItems();

// Group items by their parent group label
const grouped = FLAT_ITEMS.reduce<Record<string, typeof FLAT_ITEMS>>((acc, item) => {
  const key = item.group ?? 'Quick Access';
  if (!acc[key]) acc[key] = [];
  acc[key].push(item);
  return acc;
}, {});

interface CommandSearchProps {
  /** Controlled open state — pass if you want to trigger externally */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CommandSearch = ({ open: controlledOpen, onOpenChange }: CommandSearchProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const navigate = useNavigate();
  const { unlockedPaths, unlockPath } = useUIStore();

  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState<{ path: string; label: string } | null>(null);

  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback(
    (val: boolean) => {
      setInternalOpen(val);
      onOpenChange?.(val);
    },
    [onOpenChange],
  );

  // Keyboard shortcut — Ctrl+K / ⌘K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, setOpen]);

  const handleSelect = (item: NavItem) => {
    if (!item.path) return;
    const isLocked = item.isPrivate && !unlockedPaths[item.path];
    if (isLocked) {
      setPendingNav({ path: item.path, label: item.label });
      setIsPinModalOpen(true);
      setOpen(false);
    } else {
      setOpen(false);
      navigate(item.path);
    }
  };

  const handlePinSuccess = () => {
    if (pendingNav) {
      unlockPath(pendingNav.path);
      setIsPinModalOpen(false);
      navigate(pendingNav.path);
      setPendingNav(null);
    }
  };

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search modules, pages…" />
        <CommandList>
          <CommandEmpty>
            <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground">
              <Search size={32} className="opacity-30" />
              <p className="text-sm">No modules found.</p>
            </div>
          </CommandEmpty>

          {Object.entries(grouped).map(([groupName, items]) => (
            <CommandGroup key={groupName} heading={groupName}>
              {items.map((item) => {
                const Icon = item.icon;
                const isLocked = item.isPrivate && !unlockedPaths[item.path!];
                return (
                  <CommandItem
                    key={item.path}
                    value={`${item.label} ${groupName}`}
                    onSelect={() => handleSelect(item)}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <span
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-lg',
                        'bg-primary/10 text-primary',
                      )}
                    >
                      <Icon size={15} />
                    </span>
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium">{item.label}</span>
                        {isLocked && <Lock size={12} className="text-muted-foreground/70" />}
                      </div>
                      {item.group && (
                        <span className="text-[11px] text-muted-foreground">{item.group}</span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>

      <PinVerificationModal
        isOpen={isPinModalOpen}
        onClose={() => {
          setIsPinModalOpen(false);
          setPendingNav(null);
        }}
        onSuccess={handlePinSuccess}
      />
    </>
  );
};

export default CommandSearch;
